import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import {
  addCostType,
  fetchCostType,
} from "../../../../../services/orders.services";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import { useDispatch } from "react-redux";
import { showSnackBar } from "../../../../../store/common/snackBarSlice";
import { useTranslation } from "react-i18next";

const filter = createFilterOptions();

export default function CostTypeDropdownWithModal({
  setValue,
  value,
  setcost,
}) {
  const { t } = useTranslation(["warehouse", "common"]);
  const [costs, setCosts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [newCostType, setNewCostType] = useState({ name: "", cost_value: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => {
    setModalOpen(false);
    setNewCostType({ name: "", cost_value: 0 });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCostType((prev) => ({ ...prev, [name]: value }));
  };

  const addNewCosttype = async () => {
    if (!newCostType.name) {
      dispatch(
        showSnackBar({
          text: t("orderDetails.costTypeNameIsRequired"),
          severity: "error",
        })
      );
      return;
    }

    setIsLoading(true);
    try {
      const response = await addCostType(newCostType);
      if (!response.error) {
        setCosts([...costs, response?.data?.result]);
        setValue({
          id: response?.data?.result?.id,
          name: newCostType.name,
          cost_value: newCostType.cost_value,
        });
        setcost(newCostType.cost_value);
        handleCloseModal();
      }
    } catch (error) {
      dispatch(
        showSnackBar({
          text: t("orderDetails.failedToAddCostType"),
          severity: "error",
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getCostType = async () => {
    try {
      const response = await fetchCostType();
      if (!response.error) {
        setCosts(response?.data?.result);
      }
    } catch (error) {
      console.error("Error fetching cost types:", error);
    }
  };

  useEffect(() => {
    getCostType();
  }, []);

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[500000000000000] bg-[gray] bg-opacity-30"
        onClick={handleCloseModal}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[500000000000000] flex items-center justify-center ">
        <div
          className="w-full max-w-md mx-4 bg-ui-surface rounded-lg shadow-xl overflow-hidden py-3 pb-2"
          onClick={(e) => e.stopPropagation()} // Prevent click from closing modal
        >
          {/* Header */}
          <div className="px-6  border-b border-ui-border">
            <h3 className="text-lg font-medium">{t("orderDetails.addNewCostType")}</h3>
          </div>

          {/* Body */}
          <div className="px-6 py-2 space-y-2">
            <div>
              <label className="block text-sm font-normal text-ui-text mb-1">
                {t("orderDetails.costTypeName")}
              </label>
              <input
                type="text"
                name="name"
                value={newCostType.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-ui-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
              />
            </div>

            <div>
              <label className="block text-sm font-normal text-ui-text mb-1">
                {t("orderDetails.costValue")}
              </label>
              <input
                type="number"
                name="cost_value"
                value={newCostType.cost_value}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-ui-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-3 bg-ui-surface flex justify-end space-x-3">
            <button
              onClick={handleCloseModal}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-normal text-ui-text bg-ui-surface border border-ui-border rounded-md hover:bg-ui-surface-sunken"
            >
              {t("common:actions.cancel")}
            </button>
            <button
              onClick={addNewCosttype}
              disabled={isLoading}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-brand ${
                isLoading
                  ? "bg-brand cursor-not-allowed"
                  : "bg-brand hover:bg-brand-hover"
              }`}
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t("common:actions.add")
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Autocomplete
        value={value}
        onChange={(event, newValue) => {
          if (typeof newValue === "string") {
            const selectedOption = costs.find(
              (option) => option.name === newValue
            );
            setValue(selectedOption);
          } else if (newValue && newValue.inputValue) {
            setNewCostType({ name: newValue.inputValue, cost_value: 0 });
            setModalOpen(true);
          } else {
            setValue(newValue);
            setcost(newValue?.cost_value);
          }
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          const { inputValue } = params;
          const isExisting = options.some(
            (option) => inputValue === option.name
          );
          if (inputValue !== "" && !isExisting) {
            filtered.push({ inputValue, name: `Add "${inputValue}"` });
          }
          return filtered;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        id="cost-type-dropdown"
        options={costs}
        getOptionLabel={(option) => {
          if (typeof option === "string") return option;
          if (option.inputValue) return option.inputValue;
          return `${option.name} (${option.cost_value ?? 0})`;
        }}
        renderOption={(props, option) => (
          <li {...props}>{`${option.name} (${option.cost_value ?? 0})`}</li>
        )}
        sx={{ width: 300 }}
        freeSolo
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("orderDetails.costType")}
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "var(--color-ui-border)" },
                "&:hover fieldset": { borderColor: "var(--color-brand)" },
                "&.Mui-focused fieldset": {
                  borderColor: "var(--color-brand)",
                  borderWidth: 2,
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "var(--color-brand)",
              },
              "& .MuiAutocomplete-clearIndicator": {
                marginTop: "2px",
              },
            }}
          />
        )}
      />
      {modalOpen && ReactDOM.createPortal(modalContent, document.body)}
    </Box>
  );
}
